import { JSONContent } from '@tiptap/core'

import type { TypeInstanceMap } from './data'
import type { VariableFieldADTValue } from './field'
import type { FilterFormType } from './filter'
import type { Sorter } from './general'

export enum EVENT_VARIABLE_VALUE {
    'PAGE_NAME' = 'PAGE_NAME',
    'BLOCK_NAME' = 'BLOCK_NAME',
    'EVENT_TYPE' = 'EVENT_TYPE',
    'CLIENT_TYPE' = 'CLIENT_TYPE',
    'EVENT_NAME' = 'EVENT_NAME'
}

export enum EVENT_VARIABLE_TYPE {
    'PAGE_VISIT' = 'PAGE_VISIT',
    // 'WECHAT_SHARE' = 'WECHAT_SHARE',
    'BUTTON_CLICK' = 'BUTTON_CLICK'
}

export enum LOOP_VARIABLE_VALUE {
    'LOOP_TIMES' = 'LOOP_TIMES'
}

export const DATE_VARIABLE = [
    'NOW',
    'TODAY',
    'TOMORROW',
    'YESTERDAY',
    'THIS_WEEK',
    'LAST_WEEK',
    'THIS_MONTH',
    'LAST_MONTH',
    'THIS_YEAR',
    'PAST_SEVEN_DAYS',
    'FUTURE_SEVEN_HEAVENS',
    'PAST_THIRTY_DAYS',
    'FUTURE_THIRTY_HEAVENS',
    'BEFORE_SOMEWHAT_DAYS',
    'AFTER_SOMEWHAT_DAYS'
] as const

export const DATE_RANGE_VARIABLE = [
    'TODAY',
    'TOMORROW',
    'YESTERDAY',
    'THIS_WEEK',
    'LAST_WEEK',
    'NEXT_WEEK',
    'THIS_MONTH',
    'LAST_MONTH',
    'NEXT_MONTH',
    'THIS_QUARTER',
    'LAST_QUARTER',
    'NEXT_QUARTER',
    'THIS_YEAR',
    'LAST_YEAR',
    'NEXT_YEAR',
    'PAST_SEVEN_DAYS',
    'FUTURE_SEVEN_HEAVENS',
    'PAST_FOURTEEN_DAYS',
    'FUTURE_FOURTEEN_HEAVENS',
    'PAST_THIRTY_DAYS',
    'FUTURE_THIRTY_HEAVENS'
] as const

export type DateRangeVariableValue = typeof DATE_RANGE_VARIABLE[number]

export type DateVariableValue = typeof DATE_VARIABLE[number]

export enum VariableType {
    'VARIABLE' = 'VARIABLE',
    'VALUE' = 'VALUE',
    'ARG_VARIABLE' = 'ARG_VARIABLE',
    'UPSTREAM' = 'UPSTREAM',
    // 'AGGREGATOR' = 'AGGREGATOR',
    'FIELD_ID' = 'FIELD_ID',
    'SYSTEM' = 'SYSTEM',
    'USER' = 'USER',
    'INPUT' = 'INPUT',
    'FORM' = 'FORM',
    'VIEW' = 'VIEW',
    'ROUTE_PATH' = 'ROUTE_PATH',
    'ROUTE_QUERY' = 'ROUTE_QUERY',
    'PAGE_DATA_QUERY' = 'PAGE_DATA_QUERY',
    'PAGE_STATE' = 'PAGE_STATE',
    'PAGE_LINK' = 'PAGE_LINK',
    'DEVICE_ID' = 'DEVICE_ID',
    'FILTER' = 'FILTER',
    'SELECT_DATASOURCE' = 'SELECT_DATASOURCE',
    'RESOLVED' = 'RESOLVED',
    'EVENT' = 'EVENT',
    'LOOP' = 'LOOP',
    'FORMULA' = 'FORMULA',
    'IMAGE' = 'IMAGE'
}

export enum FilterMode {
    'CUSTOM' = 'CUSTOM',
    'NORMAL' = 'NORMAL'
    // 'WORKFLOW' = 'WORKFLOW'
}

/** 链接表用到的变量 */
export type FieldVariableConfig = {
    // type: 'VARIABLE'
    fieldVariable?: {
        dsId?: string
        fieldId?: string
    }
}

/** 用户数据的变量 */
export type UserVariableConfig = {
    // type: 'USER'
    userVariable?: {
        fieldId?: string
        format?: string
    }
}

/** 数据源及视图用到的变量 */
export type FieldIdVariableConfig = {
    // type: fieldId
    fieldIdVariable?: {
        fieldId?: string
    }
}

export type ValueVariableConfig = {
    valueVariable?: VariableFieldADTValue
}

// 子流程
export type ArgVariableConfig = {
    // type: upstream
    argVariable?: {
        nodeId?: string
        argId?: string
    }
}

export type UpstreamVariableConfig = {
    // type: upstream
    upstreamVariable?: {
        nodeId?: string
        fieldId?: string
    }
}

export type AggregatorVariableConfig = {
    // type: upstream
    aggregatorVariable?: {
        nodeId?: string
        fieldId?: string
    }
}

export type SystemVariableConfig = {
    // type: upstream
    systemVariable?: {
        value?: DateVariableValue
        format?: string
    }
}

//  输入框的值
export type InputVariableConfig = {
    inputVariable?: {
        blockId: string
        scope?: string
    }
}

// 表单容器
export type FormVariableConfig = {
    // type: 'USER'
    formVariable?: {
        fieldId?: string
    }
}

// 自定义视图
export type ViewVariableConfig = {
    viewVariable?: {
        fieldId: string
        format?: string
    }
}
// 页面链接
export type PageLinkVariableConfig = {
    pageLinkVariable?: {
        value: 'CURRENT_PAGE' | undefined
    }
}

export const DEVICE_ID_VALUE = 'DEVICE_ID' as const

// 设备ID
export type DeviceIdVariableConfig = {
    deviceIdVariable?: {
        value: typeof DEVICE_ID_VALUE | undefined
    }
}

// 筛选控制器
export type FilterVariableConfig = {
    filterVariable?: {
        scope?: string
        blockId: string
        itemId: string
    }
}

export enum ImageVariableValue {
    'IMAGE_NAME' = 'IMAGE_NAME',
    'IMAGE_URL' = 'IMAGE_URL',
    'IMAGE_INDEX' = 'IMAGE_INDEX'
}
// 图片
export type ImageVariableConfig = {
    imageVariable?: {
        dataKey: ImageVariableValue
    }
}
export type RoutePathVariableConfig = {
    routePathVariable?: {
        id: string
    }
}
export type RouteQueryVariableConfig = {
    routeQueryVariable?: {
        id: string
    }
}
/** 页面数据变量 */
export type PageDataQueryVariableConfig = {
    pageDataQueryVariable?: {
        /** 查询器id */
        id: string
        /** 变量id */
        itemId: string
        format?: string
    }
}

export type PageStateVariableConfig = {
    pageStateVariable?: {
        id: string
    }
}

// 查询数据表
export type SelectDataSourceVariableConfig = {
    selectDataSourceVariable?: {
        dsId: string
        fieldId: string
        sorts: Sorter[]
        filter: FilterFormType
        // filterWhere: 'AND' | 'OR'
    }
}

export type ResolvedVariableConfig = {
    resolvedVariable?: VariableFieldADTValue & {
        innerType: TypeInstanceMap | undefined
    }
}

export type EventVariableConfig = {
    eventVariable?: {
        // type: EVENT_VARIABLE_TYPE
        value: EVENT_VARIABLE_VALUE
    }
}

export type LoopVariableConfig = {
    loopVariable?: {
        value: LOOP_VARIABLE_VALUE
    }
}

export type FormulaVariableConfig = {
    formulaVariable?: {
        value: JSONContent
    }
}

export type FieldVariable = Record<'type', VariableType.VARIABLE> & FieldVariableConfig

export type FieldIdVariable = Record<'type', VariableType.FIELD_ID> & FieldIdVariableConfig

export type ValueVariable = Record<'type', VariableType.VALUE> & ValueVariableConfig

export type ArgVariable = Record<'type', VariableType.ARG_VARIABLE> & ArgVariableConfig

export type UpstreamVariable = Record<'type', VariableType.UPSTREAM> & UpstreamVariableConfig

export type SystemVariable = Record<'type', VariableType.SYSTEM> & SystemVariableConfig

export type UserVariable = Record<'type', VariableType.USER> & UserVariableConfig

export type InputVariable = Record<'type', VariableType.INPUT> & InputVariableConfig

export type FormVariable = Record<'type', VariableType.FORM> & FormVariableConfig

export type ViewVariable = Record<'type', VariableType.VIEW> & ViewVariableConfig

export type RoutePathVariable = Record<'type', VariableType.ROUTE_PATH> & RoutePathVariableConfig
export type RouteQueryVariable = Record<'type', VariableType.ROUTE_QUERY> & RouteQueryVariableConfig
export type PageVariable = Record<'type', VariableType.PAGE_DATA_QUERY> & PageDataQueryVariableConfig
export type PageStateVariable = Record<'type', VariableType.PAGE_STATE> & PageStateVariableConfig

export type PageLinkVariable = Record<'type', VariableType.PAGE_LINK> & PageLinkVariableConfig

export type DeviceIdVariable = Record<'type', VariableType.DEVICE_ID> & DeviceIdVariableConfig

export type FilterVariable = Record<'type', VariableType.FILTER> & FilterVariableConfig

export type SelectDataSourceVariable = Record<'type', VariableType.SELECT_DATASOURCE> & SelectDataSourceVariableConfig

export type ResolvedValueVariable = Record<'type', VariableType.RESOLVED> & ResolvedVariableConfig

export type EventValueVariable = Record<'type', VariableType.EVENT> & EventVariableConfig

export type LoopVariable = Record<'type', VariableType.LOOP> & LoopVariableConfig

export type FormulaVariable = Record<'type', VariableType.FORMULA> & FormulaVariableConfig

export type ImageVariable = Record<'type', VariableType.IMAGE> & ImageVariableConfig

export type VariableADTvalue =
    | FieldVariable
    | ValueVariable
    | ArgVariable
    | UpstreamVariable
    | FieldIdVariable
    | SystemVariable
    | UserVariable
    | InputVariable
    | FormVariable
    | ViewVariable
    | RoutePathVariable
    | RouteQueryVariable
    | PageVariable
    | PageStateVariable
    | PageLinkVariable
    | DeviceIdVariable
    | FilterVariable
    | SelectDataSourceVariable
    | ResolvedValueVariable
    | EventValueVariable
    | LoopVariable
    | FormulaVariable
    | ImageVariable
